import { useParams, Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import docs from "../data/docsContent";
import { sections } from "../components/DocsLayout";

const CodeBlock = ({ children }) => (
  <pre className="bg-[#f8f9fa] border border-[#e8eaed] rounded-lg p-4 overflow-x-auto text-[13px] leading-relaxed font-mono text-[#202124]">
    <code>{children}</code>
  </pre>
);

const renderMarkdown = (content) => {
  const lines = content.trim().split("\n");
  const elements = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Code block
    if (line.trim().startsWith("```")) {
      const codeLines = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith("```")) {
        codeLines.push(lines[i]);
        i++;
      }
      i++; // skip closing ```
      elements.push(<CodeBlock key={key++}>{codeLines.join("\n")}</CodeBlock>);
      continue;
    }

    // Table
    if (line.includes("|") && line.trim().startsWith("|")) {
      const tableLines = [];
      while (i < lines.length && lines[i].includes("|") && lines[i].trim().startsWith("|")) {
        tableLines.push(lines[i]);
        i++;
      }
      if (tableLines.length >= 2) {
        const headers = tableLines[0].split("|").filter(c => c.trim()).map(c => c.trim());
        const rows = tableLines.slice(2).map(row =>
          row.split("|").filter(c => c.trim()).map(c => c.trim())
        );
        elements.push(
          <div key={key++} className="overflow-x-auto my-4">
            <table className="w-full text-[14px] border-collapse">
              <thead>
                <tr className="border-b-2 border-[#e8eaed]">
                  {headers.map((h, hi) => (
                    <th key={hi} className="text-left py-2.5 px-3 font-medium text-[#202124] whitespace-nowrap">{formatInline(h)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, ri) => (
                  <tr key={ri} className="border-b border-[#e8eaed]">
                    {row.map((cell, ci) => (
                      <td key={ci} className="py-2.5 px-3 text-[#5f6368]">{formatInline(cell)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        continue;
      }
    }

    // H2
    if (line.startsWith("## ")) {
      elements.push(<h2 key={key++} className="text-[24px] sm:text-[28px] font-normal text-[#202124] mt-10 mb-4 leading-tight">{line.slice(3)}</h2>);
      i++;
      continue;
    }

    // H3
    if (line.startsWith("### ")) {
      elements.push(<h3 key={key++} className="text-[18px] font-medium text-[#202124] mt-8 mb-3">{line.slice(4)}</h3>);
      i++;
      continue;
    }

    // Empty line
    if (line.trim() === "") {
      i++;
      continue;
    }

    // Regular paragraph
    elements.push(<p key={key++} className="text-[15px] text-[#5f6368] leading-relaxed mb-4">{formatInline(line)}</p>);
    i++;
  }

  return elements;
};

const formatInline = (text) => {
  if (!text) return text;
  const parts = [];
  let remaining = text;
  let k = 0;

  while (remaining.length > 0) {
    // Bold
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    // Inline code
    const codeMatch = remaining.match(/`(.+?)`/);
    // Link
    const linkMatch = remaining.match(/\[(.+?)\]\((.+?)\)/);

    const matches = [
      boldMatch ? { type: "bold", index: boldMatch.index, match: boldMatch } : null,
      codeMatch ? { type: "code", index: codeMatch.index, match: codeMatch } : null,
      linkMatch ? { type: "link", index: linkMatch.index, match: linkMatch } : null,
    ].filter(Boolean).sort((a, b) => a.index - b.index);

    if (matches.length === 0) {
      parts.push(remaining);
      break;
    }

    const first = matches[0];
    if (first.index > 0) {
      parts.push(remaining.slice(0, first.index));
    }

    if (first.type === "bold") {
      parts.push(<strong key={k++} className="font-medium text-[#202124]">{first.match[1]}</strong>);
      remaining = remaining.slice(first.index + first.match[0].length);
    } else if (first.type === "code") {
      parts.push(<code key={k++} className="px-1.5 py-0.5 bg-[#f1f3f4] rounded text-[13px] font-mono text-[#d93025]">{first.match[1]}</code>);
      remaining = remaining.slice(first.index + first.match[0].length);
    } else if (first.type === "link") {
      parts.push(<Link key={k++} to={first.match[2]} className="text-[#6C5CE7] hover:text-[#e8872e] underline">{first.match[1]}</Link>);
      remaining = remaining.slice(first.index + first.match[0].length);
    }
  }

  return parts.length === 1 && typeof parts[0] === "string" ? parts[0] : parts;
};

const DocPage = () => {
  const { category, page } = useParams();
  const docKey = `${category}/${page}`;
  const doc = docs[docKey];

  // Find next/prev pages
  const allPages = sections.flatMap(s => s.items);
  const currentPath = `/docs/${docKey}`;
  const currentIndex = allPages.findIndex(p => p.path === currentPath);
  const prev = currentIndex > 0 ? allPages[currentIndex - 1] : null;
  const next = currentIndex < allPages.length - 1 ? allPages[currentIndex + 1] : null;

  if (!doc) {
    return (
      <div className="max-w-3xl mx-auto px-8 py-16 text-center">
        <h1 className="text-[28px] font-normal text-[#202124] mb-4">Page not found</h1>
        <p className="text-[15px] text-[#5f6368] mb-6">This documentation page doesn't exist yet.</p>
        <Link to="/docs" className="text-[14px] font-medium text-[#6C5CE7] hover:text-[#e8872e]">Back to docs</Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-8 py-10" data-testid="doc-page">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-[13px] text-[#5f6368] mb-8" data-testid="doc-breadcrumb">
        <Link to="/docs" className="hover:text-[#202124]">Docs</Link>
        {doc.breadcrumb.map((crumb, i) => (
          <span key={i} className="flex items-center gap-1.5">
            <ChevronRight className="w-3 h-3" />
            <span className={i === doc.breadcrumb.length - 1 ? "text-[#202124] font-medium" : ""}>{crumb}</span>
          </span>
        ))}
      </nav>

      {/* Title */}
      <h1 className="text-[32px] sm:text-[36px] font-normal text-[#202124] mb-8 leading-tight" data-testid="doc-title">
        {doc.title}
      </h1>

      {/* Content */}
      <div className="docs-content" data-testid="doc-content">
        {renderMarkdown(doc.content)}
      </div>

      {/* Prev/Next navigation */}
      <div className="mt-16 pt-8 border-t border-[#e8eaed] flex items-center justify-between" data-testid="doc-nav">
        {prev ? (
          <Link to={prev.path} className="group flex flex-col items-start">
            <span className="text-[12px] text-[#9aa0a6] mb-1">Previous</span>
            <span className="text-[14px] font-medium text-[#6C5CE7] group-hover:text-[#e8872e]">{prev.label}</span>
          </Link>
        ) : <div />}
        {next ? (
          <Link to={next.path} className="group flex flex-col items-end">
            <span className="text-[12px] text-[#9aa0a6] mb-1">Next</span>
            <span className="text-[14px] font-medium text-[#6C5CE7] group-hover:text-[#e8872e]">{next.label}</span>
          </Link>
        ) : <div />}
      </div>
    </div>
  );
};

export default DocPage;
